import { useMutation } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { endpoints, Url } from '@rossum/api-client';
import { SuggestRuleResponse } from '@rossum/api-client/internal';
import {
  QueueRuleAction,
  queueRuleActionSchema,
} from '@rossum/api-client/schemas';
import { api } from '../../../lib/apiClient';
import { throwError } from '../../../redux/modules/messages/actions';

export type RuleTemplate = {
  name: string;
  enabled: boolean;
  description: string;
  triggerCondition: string;
  triggerConditionSummary: string;
  actions: QueueRuleAction[];
};

const parseRuleActions = (ruleActions: unknown): QueueRuleAction[] => {
  if (!Array.isArray(ruleActions)) return [];

  return ruleActions
    .map(action => {
      const parsedRuleAction = queueRuleActionSchema.safeParse(action);
      return parsedRuleAction.success ? parsedRuleAction.data : undefined;
    })
    .filter((action): action is QueueRuleAction => action !== undefined);
};

export const narrowRuleSuggestionResultToTemplate = (
  data: SuggestRuleResponse
): RuleTemplate[] =>
  data.results.map(r => ({
    name: r.name ?? '',
    enabled: r.enabled ?? true,
    description: r.description ?? '',
    triggerCondition: r.triggerCondition ?? '',
    triggerConditionSummary: r.triggerConditionSummary ?? '',
    actions: parseRuleActions(r.actions),
  }));

export const useSuggestRule = () => {
  const dispatch = useDispatch();
  const mutation = useMutation({
    mutationFn: ({ search, queue }: { search: string; queue: Url }) =>
      api.request(
        endpoints.internal.rules.suggestRule({
          userQuery: search,
          queue,
        })
      ),
    onError: () => {
      dispatch(throwError('clientError'));
    },
  });

  return mutation;
};
